import { CitizenDTO } from '../dto/citizen.dto';
import { ItemCountDTO } from '../dto/item-count.dto';
import { ShortItemCountDTO } from '../dto/short-item-count.dto';
import { JobEnum } from '../enum/job.enum';
import { StatusEnum } from '../enum/status.enum';
import { TownRoleEnum } from '../enum/town-role.enum';
import { CommonModel, dtoToModelArray, modelToDtoArray } from './_common.class';
import { Dictionary } from './_types';
import { Bag } from './bag.class';
import { Cadaver } from './cadaver.class';
import { ChamanicDetail } from './chamanic-detail.class';
import { DailyAction } from './daily-action.class';
import { heroSkillLevelForJob, HeroSkillLevels, toHeroSkillLevel } from './hero-skill-levels';
import { HeroicActions, HeroicActionsWithValue } from './heroic-actions.class';
import { Home, HomeWithValue } from './home.class';
import { Status } from './status.class';

export class Citizen extends CommonModel<CitizenDTO> {
    public avatar?: string;
    public id!: number;
    public is_shunned?: boolean;
    public is_dead?: boolean;
    public job?: JobEnum;
    /** Rôles de ville portés. Une liste : un citoyen peut en cumuler plusieurs. */
    public town_roles: TownRoleEnum[] = [];
    public name!: string;
    /** Défense apportée par la maison seule (`baseDef` MyHordes), hors job/objets/actions héroïques. */
    public house_defense?: number;
    public bag?: Bag;
    public status?: Status;
    public home?: Home;
    public heroic_actions?: HeroicActions;
    public cadaver?: Cadaver;
    public daily_actions: DailyAction[] = [];
    public chamanic_detail!: ChamanicDetail;
    private home_message?: string;
    private nombre_jour_hero?: number;
    private x?: number;
    private y?: number;
    public chest?: Bag;
    /** Campings déjà effectués dans la ville, saisis à la main ; 0 si rien n'est saisi. */
    public camping_count: number = 0;
    /** Chaussures équipées, déclarées par le citoyen dans la liste des citoyens ou envoyées par le script ; `false` si rien n'est déclaré. */
    public shoes_equipped: boolean = false;
    /** Niveaux des compétences héroïques, saisis à la main. */
    public hero_skills: HeroSkillLevels = { s: null, u: null, p: null, e: null, r: null };

    public constructor(dto?: CitizenDTO) {
        super();
        this.dtoToModel(dto);
    }

    public modelToDto(): CitizenDTO {
        return {
            avatar: this.avatar,
            homeMessage: this.home_message,
            id: this.id,
            isShunned: this.is_shunned,
            dead: this.is_dead,
            jobName: '',
            jobUid: this.job?.key,
            townRoles: this.town_roles.map((role: TownRoleEnum): string => role.key),
            nombreJourHero: this.nombre_jour_hero,
            x: this.x,
            y: this.y,
            houseDefense: this.house_defense,
            name: this.name,
            bag: this.bag?.modelToDto(),
            chest: this.chest?.modelToDto(),
            status: this.status?.modelToDto(),
            home: this.home?.modelToDto(),
            actionsHeroic: this.heroic_actions?.modelToDto(),
            dailyActions: this.daily_actions ? modelToDtoArray(this.daily_actions) : [],
            chamanicDetail: this.chamanic_detail.modelToDto(),
            cadaver: this.cadaver?.modelToDto(),
            campingCount: this.camping_count,
            shoesEquipped: this.shoes_equipped,
            heroSkills: { ...this.hero_skills }
        };
    }

    public toCitizenBagDto(): { userId: number, objects: ShortItemCountDTO[] } {
        return {
            userId: this.id,
            objects: this.bag?.modelToDto().items.map((item: ItemCountDTO) => {
                return {
                    count: item.count,
                    id: item.item.id,
                    isBroken: item.isBroken
                };
            }) || [],
        };
    }

    public toCitizenChestDto(): { userId: number, objects: ShortItemCountDTO[] } {
        return {
            userId: this.id,
            objects: this.chest?.modelToDto().items.map((item: ItemCountDTO) => {
                return {
                    count: item.count,
                    id: item.item.id,
                    isBroken: item.isBroken
                };
            }) || [],
        };
    }

    public toCitizenStatusDto(): { userId: number, status: string[] } {
        return {
            userId: this.id,
            status: this.status?.icons.map((icon: StatusEnum) => icon?.key) || [],
        };
    }

    public toCitizenHeroicActionsDto(): { userId: number, heroicActions: Dictionary<number | boolean> } {
        return {
            userId: this.id,
            heroicActions: this.heroic_actions?.content.reduce((accumulator: Dictionary<number | boolean>, content: HeroicActionsWithValue) => {
                return { ...accumulator, [content.element?.key]: content.value };
            }, {}) || {}
        };
    }

    public toCitizenHomeDto(): { userId: number, home: Dictionary<number | boolean> } {
        return {
            userId: this.id,
            home: this.home?.content.reduce((accumulator: Dictionary<number | boolean>, content: HomeWithValue) => {
                return { ...accumulator, [content.element?.key]: content.value };
            }, {}) || {}
        };
    }

    public getTag(): string {
        return `@${this.name}:${this.id}`;
    }

    protected dtoToModel(dto?: CitizenDTO): void {
        if (dto) {
            this.avatar = dto.avatar;
            this.home_message = dto.homeMessage;
            this.id = dto.id;
            this.is_shunned = dto.isShunned;
            this.is_dead = dto.dead;
            this.job = dto.jobUid ? <JobEnum>JobEnum.getByKey(dto.jobUid) : undefined;
            this.town_roles = (dto.townRoles ?? [])
                .map((key: string) => TownRoleEnum.getByKey<TownRoleEnum>(key))
                .filter((role: TownRoleEnum | undefined): role is TownRoleEnum => !!role);
            this.nombre_jour_hero = dto.nombreJourHero;
            this.x = dto.x;
            this.y = dto.y;
            this.house_defense = dto.houseDefense;
            this.name = dto.name;
            this.bag = new Bag(dto.bag);
            this.chest = new Bag(dto.chest);
            this.status = new Status(dto.status);
            this.home = new Home(dto.home);
            this.heroic_actions = new HeroicActions(dto.actionsHeroic);
            this.daily_actions = dtoToModelArray(DailyAction, dto.dailyActions);
            this.chamanic_detail = new ChamanicDetail(dto.chamanicDetail);
            this.cadaver = dto.cadaver ? new Cadaver(dto.cadaver) : undefined;
            this.camping_count = typeof dto.campingCount === 'number' ? dto.campingCount : 0;
            this.shoes_equipped = dto.shoesEquipped === true;
            this.hero_skills = {
                s: heroSkillLevelForJob(this.job, toHeroSkillLevel(dto.heroSkills?.s)),
                u: heroSkillLevelForJob(this.job, toHeroSkillLevel(dto.heroSkills?.u)),
                p: heroSkillLevelForJob(this.job, toHeroSkillLevel(dto.heroSkills?.p)),
                e: heroSkillLevelForJob(this.job, toHeroSkillLevel(dto.heroSkills?.e)),
                r: heroSkillLevelForJob(this.job, toHeroSkillLevel(dto.heroSkills?.r))
            };
        }
    }
}
